import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authService } from '../lib/api/apiService';
import { cartAPI } from '../api/endpoints';
import useCurrentUser from './useCurrentUser';
import { getCartQueryKey } from './useCartQuery';

/**
 * Hook personnalisé pour gérer les opérations sur le panier (suppression, mise à jour)
 */
const useCartOperations = () => {
  const queryClient = useQueryClient();

  const { data: user } = useCurrentUser();
  const isAuthenticated = authService.isAuthenticated();
  const userId = user?._id || user?.id;

  /**
   * Supprime un item du panier via l'API
   * @param {string} itemId - ID de l'item sur le serveur
   * @param {Function} onSuccess - Callback appelé après succès
   * @param {Function} onError - Callback appelé en cas d'erreur
   */
  const removeItemFromAPI = useCallback(async (itemId, onSuccess, onError) => {
    if (!isAuthenticated || !userId || !itemId) {
      if (onError) onError(new Error('Utilisateur non connecté ou itemId manquant'));
      return;
    }

    try {
      await cartAPI.deleteCartItem(userId, itemId);
      queryClient.invalidateQueries({ queryKey: getCartQueryKey(userId) });
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('❌ Erreur lors de la suppression:', error);
      if (onError) onError(error);
    }
  }, [isAuthenticated, userId, queryClient]);

  /**
   * Met à jour la quantité d'un item du panier via l'API
   * @param {string} itemId - ID de l'item sur le serveur
   * @param {number} quantity - Nouvelle quantité
   * @param {Function} onSuccess - Callback appelé après succès
   * @param {Function} onError - Callback appelé en cas d'erreur
   */
  const updateItemQuantityInAPI = useCallback(async (itemId, quantity, onSuccess, onError) => {
    if (!isAuthenticated || !userId || !itemId) {
      if (onError) onError(new Error('Utilisateur non connecté ou itemId manquant'));
      return;
    }

    try {
      await cartAPI.updateCartItem(userId, itemId, { quantity });
      queryClient.invalidateQueries({ queryKey: getCartQueryKey(userId) });
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour:', error);
      if (onError) onError(error);
    }
  }, [isAuthenticated, userId, queryClient]);

  return {
    removeItemFromAPI,
    updateItemQuantityInAPI,
    isAuthenticated,
    userId,
  };
};

export default useCartOperations;

